import React, {ReactNode, useState} from 'react'
import {FormattedDate, useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'

import {SplitBookingInstructionModal} from '../SplitBookingInstructionModal'

interface BookingTransitionMessageProps {
  midDate: string
  checkIn: string
  checkOut: string
  onButtonClick?: () => void
  title: ReactNode
  message: ReactNode
  hasCustomStyle?: boolean
  /** B side of `d2314dca-pp-information-rework`*/
  showInformationRework?: boolean
}

const StyledButtonLink = styled(ButtonLink)`
  min-height: unset;
  text-decoration-color: ${cssTheme.colors.content.neutral
    .secondary} !important;

  &:hover {
    text-decoration-color: ${cssTheme.colors.content.neutral.secondary};
  }
`

const Container = styled(Grid)`
  padding-left: ${cssTheme.layout.spacing.s000};
  align-content: center;
  gap: 0 ${cssTheme.layout.spacing.s250};
  display: flex;
  align-items: center;
  flex: 1;
  margin-left: -1px;
  flex-wrap: wrap;
  &.--has-custom-style {
    padding-left: ${cssTheme.layout.spacing.s400};
  }
  &.--information-rework {
    justify-content: space-between;
  }
`

export const BookingTransitionMessage = ({
  midDate,
  checkIn,
  checkOut,
  onButtonClick,
  title,
  message,
  hasCustomStyle = false,
  showInformationRework = false
}: BookingTransitionMessageProps) => {
  const [isInstructionPopUpOpen, setIsInstructionPopUpOpen] = useState(false)
  const intl = useIntl()
  const handleOnClick = () => {
    setIsInstructionPopUpOpen(true)
    onButtonClick?.()
  }
  const formattedMidDate = intl.formatDate(midDate, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC'
  })
  const midDay = (
    <FormattedDate value={dateStringToMiddayDate(midDate)} weekday="short" />
  )
  return (
    <Container
      className={cx(
        hasCustomStyle && '--has-custom-style',
        showInformationRework && '--information-rework'
      )}
    >
      <Text
        variant={hasCustomStyle ? 'labelM' : 'labelS'}
        dataId="splitBooking.midStayText"
      >
        {showInformationRework ? (
          <FormattedMessageWrapper
            id="splitBooking.switchRoomsOn"
            defaultMessage="Switch rooms on {midDay}, {midDate}."
            values={{midDay, midDate: formattedMidDate}}
          />
        ) : (
          <FormattedMessageWrapper
            id="splitBooking.midStayText"
            defaultMessage="Room switch required {midDay}, {midDate}."
            values={{midDay, midDate: formattedMidDate}}
          />
        )}
      </Text>
      <StyledButtonLink
        variant="primary"
        size="lg"
        onClick={handleOnClick}
        isCompact
      >
        <Text
          variant={hasCustomStyle ? 'labelM' : 'labelS'}
          tag="span"
          dataId="splitBooking.howItWorksButton"
        >
          {showInformationRework ? (
            <FormattedMessageWrapper
              id="splitBooking.howItWorks"
              defaultMessage="How it works"
            />
          ) : (
            <FormattedMessageWrapper id="why" defaultMessage="Why?" />
          )}
        </Text>
      </StyledButtonLink>
      {isInstructionPopUpOpen && (
        <SplitBookingInstructionModal
          isOpen={isInstructionPopUpOpen}
          title={title}
          message={message}
          checkInDate={checkIn}
          checkOutDate={checkOut}
          transitionDate={midDate}
          closeModal={() => setIsInstructionPopUpOpen(false)}
        />
      )}
    </Container>
  )
}
